// RUSSIAN, complete (i18n-completeness.ts). The core keys are written inline
// below; each area's namespace comes from its own `*.ru.ts` file.
//
// Russian is still bundled with the entry chunk, because every area's
// `*.i18n.ts` imports its own `*.ru.ts` today. When those imports move here,
// the chunk split that src/i18n.ts already asks for takes effect with no
// further change.

import type { Dict } from "./en.js";
import { catalogueRu } from "../areas/office/catalogue/catalogue.i18n.js";
import { inventoryRu } from "../areas/office/inventory/inventory.i18n.js";
import { wasteRu } from "../areas/office/waste/waste.i18n.js";
import { productionRu } from "../areas/office/production/production.i18n.js";
import { onboardingRu } from "../areas/onboarding/onboarding.i18n.js";
import { qualityRu } from "../areas/office/quality/quality.i18n.js";
import { salesRu } from "../areas/office/sales/sales.i18n.js";
import { messagingRu } from "../areas/office/messaging/messaging.i18n.js";
import { publicRu } from "../areas/public/public.i18n.js";
import { auditRu } from "../areas/office/audit/audit.i18n.js";
import { adminRu } from "../areas/admin/admin.i18n.js";
import { viewsRu } from "../areas/office/views/views.i18n.js";
import { planningRu } from "../areas/office/planning/planning.i18n.js";
import { agentsRu } from "../areas/office/agents/agents.i18n.js";

// RUSSIAN — translated, not a placeholder. The design partner's office staff and
// floor operators work in Russian (docs/context/kazakhstan-group.md), so ru is
// the locale that has to be real; zh/fr remain English placeholders below and
// say so in i18n-completeness.ts rather than implying otherwise.
//
// `app.title` is deliberately NOT translated: "Assessly Factory OS" is the
// product name. It is on the identical-by-design allowlist in the value gate.
export const ru: Dict = {
  app: { title: "Assessly Factory OS" },
  nav: {
    catalogue: "Каталог",
    onboarding: "Настройка",
    terminology: "Терминология",
    industryPack: "Отраслевой пакет",
    inventory: "Склад",
    waste: "Отходы",
    floor: "Цех",
    production: "Производство",
    quality: "Качество",
    sales: "Продажи",
    // Module 17: the office messaging area — «Сообщения», the word a Russian
    // office nav means by the inbox entry (never bare «Мессенджеры», which
    // names the third-party apps rather than the work surface).
    messaging: "Сообщения",
    planning: "Планирование",
    language: "Язык",
    logout: "Выйти",
    logoutPending: "На этом устройстве есть неотправленные записи: {{count}}.",
    logoutAnyway: "Выйти и потерять их",
    staySignedIn: "Остаться в системе",
    home: "Главная",
    brandName: "Assessly",
    brandSub: "Factory OS",
    searchLabel: "Глобальный поиск",
    searchPlaceholder: "Поиск: артикулы, продукты…",
    envStaging: "Стейджинг",
    setupScore: "{{score}} %",
    more: "Ещё",
    moreTitle: "Все разделы",
    moreClose: "Закрыть",
    helpAbout: "Подробнее: {{topic}}",
    moreActionsFor: "Другие действия: {{name}}",
    help: "Справка",
    helpTile: "Справка",
    helpTileSub: "Как выполнить каждую работу, с картинками",
    helpRead: "Подробнее в справке",
  },
  home: {
    title: "Операции",
    crumbRoot: "Главная",
    updated: "Обновлено {{time}}",
    refresh: "Обновить",
    loading: "Загрузка сводки…",
    day: {
      production: "Производство",
      openWos: "Открытые заказы",
      overdueWos: "Просроченные заказы",
      draftWos: "Ещё не выпущены",
      planning: "Планирование",
      schedulePublished: "График, версия {{version}}",
      scheduleNone: "График ещё не опубликован",
      scheduleUnknown: "Не удалось прочитать график",
      scheduleWaiting: "Ждёт новый план",
      lateOrders: "Опаздывающие заказы",
      overloads: "Перегруженные недели",
      actionMessages: "Сообщения к действию",
      purchases: "Закупки к размещению",
      toRelease: "Производство к выпуску",
      sales: "Продажи",
      reviews: "Открытые согласования",
      quotesToSend: "Согласованные КП к отправке",
      quotesSent: "Отправлены, ждём клиента",
      dealsStalling: "Сделки без движения",
      warehouse: "Склад",
      toReceive: "Перемещения к приёмке",
      toShip: "Заказы к отгрузке",
      quality: "Качество",
      inspections: "Проверки к выполнению",
      quarantine: "В карантине",
    },
    attention: {
      title: "Требует внимания",
      colType: "Тип",
      colItem: "Объект",
      colDetail: "Подробности",
      colAge: "Давность",
      colAction: "Действие",
      typeImport: "Импорт",
      typeSetup: "Настройка",
      typeCount: "Пересчёт",
      typeFloor: "Цех",
      typeQuality: "Качество",
      importDetail: "Отклонено строк: {{failed}} из {{total}}",
      importGroup: "Файлов импорта с отклонёнными строками: {{count}}",
      importAction: "Исправить строки",
      newest: "Последний: {{name}}",
      checklistDetail: "Нужно до запуска",
      checklistAction: "Открыть шаг",
      checklistGroup: "Проверок настройки к завершению: {{count}}",
      checklistGroupAction: "Открыть чек-лист",
      countDetail: "Пересчёт ждёт проверки",
      countGroup: "Пересчётов ждут проверки: {{count}}",
      countAction: "Проверить",
      conflictOne: "Событие цеха",
      conflictGroup: "Событий цеха: {{count}}",
      conflictUnnamed: "Событие цеха не применено",
      conflictAction: "Разобрать",
      handoffDetail: "Брак ждёт решения ({{qty}})",
      handoffGroup: "Случаев брака ждут решения: {{count}}",
      handoffUnnamed: "Брак",
      handoffAction: "Решить",
      emptyTitle: "Внимания ничего не требует",
      emptyBody: "Импорт, пересчёты, события цеха, передачи брака и ваши задачи — всё чисто.",
      emptyBodyNoTasks: "Импорт, пересчёты, события цеха и передачи брака — всё чисто.",
      showAll: "Показать все ({{count}})",
      showFewer: "Свернуть",
      range: "{{from}}–{{to}} из {{total}}",
      prevPage: "Предыдущая страница",
      nextPage: "Следующая страница",
      typeTask: "Задача",
      taskOverdue: "Просрочена на {{count}} дн",
      taskToday: "Сегодня",
      taskComplete: "Выполнить",
      taskCompleting: "Выполнение…",
      taskCompleteFailed: "Не удалось отметить задачу выполненной.",
      openMyDay: "Открыть «Мой день»",
      myDayFailed: "Не удалось прочитать ваши задачи. Откройте «Мой день», чтобы увидеть их.",
    },
    setup: {
      title: "Настройка системы",
      continue: "Продолжить: {{step}}",
      products: "Продуктов в каталоге",
      workCenters: "Рабочие центры",
    },
    floor: {
      nextLabel: "Следующее для вас",
      nextTraySub: "Офис не смог принять эти записи. Откройте их первыми.",
      nextStation: "Вернуться к {{name}}",
      nextStationUnnamed: "Вернуться к своему рабочему месту",
      nextStationSub: "Ваша очередь и следующая работа",
      nextPickStation: "Выберите рабочее место",
      nextPickStationSub: "Отсканируйте его QR или выберите из списка",
      nextCountsSub: "Откройте свои пересчёты",
      nextReceiveSub: "Посмотрите, что едет сюда",
      nextInspectSub: "Откройте очередь проверок",
      nextShipSub: "Посмотрите, что готово к сборке",
      shipNotYoursTitle: "Отгрузка не входит в вашу роль",
      shipNotYoursBody: "Обратитесь к тому, кто отгружает заказы на вашей площадке.",
      from: "Откуда: {{name}}",
      due: "Срок: {{date}}",
      started: "Начат: {{date}}",
    },
    age: {
      min: "{{count}} мин",
      h: "{{count}} ч",
      d: "{{count}} дн",
    },
  },
  login: {
    title: "Вход",
    email: "Электронная почта",
    password: "Пароль",
    submit: "Войти",
    submitting: "Выполняется вход…",
    error: "Войти не удалось. Проверьте электронную почту и пароль.",
    totpTitle: "Код двухфакторной проверки",
    totpCode: "Код подтверждения",
    totpSubmit: "Проверить",
    totpError: "Этот код не принят. Попробуйте ещё раз.",
    forgot: "Забыли пароль?",
  },
  auth: {
    suspension: {
      lockedTitle: "Доступ к рабочему пространству приостановлен",
      lockedNotice: "Доступ к рабочему пространству приостановлен.",
      lockedBilling:
        "Счёт просрочен, поэтому Assessly приостановил доступ. Свяжитесь с Assessly, чтобы восстановить его.",
      lockedOther:
        "Assessly приостановил доступ к этому рабочему пространству. Свяжитесь с вашим менеджером в Assessly, чтобы восстановить его.",
      dataKept: "Ваши данные сохранены. Ничего не удалено.",
      graceBilling:
        "Только чтение до {{date}}: счёт просрочен. Свяжитесь с Assessly, чтобы сохранить полный доступ.",
      graceOther:
        "Только чтение до {{date}}: Assessly приостановил это рабочее пространство. Свяжитесь с вашим менеджером в Assessly.",
      closedTitle: "Рабочее пространство закрыто",
      closedBody:
        "Его данные удалены по запросу владельца. Если это неожиданно, свяжитесь с Assessly.",
      floorPaused:
        "Ваша работа хранится на этом устройстве и будет отправлена, когда доступ восстановят.",
    },
    forgotTitle: "Сброс пароля",
    forgotLead: "Укажите электронную почту учётной записи — мы отправим ссылку для сброса.",
    forgotSubmit: "Отправить ссылку",
    forgotSubmitting: "Отправка…",
    forgotSent:
      "Если учётная запись с этим адресом существует, ссылка для сброса уже отправлена. Она действует 30 минут.",
    forgotError: "Не получилось. Проверьте адрес и попробуйте ещё раз.",
    backToSignIn: "Вернуться ко входу",
    resetTitle: "Новый пароль",
    resetLead: "Задайте новый пароль для своей учётной записи.",
    resetPassword: "Новый пароль",
    resetConfirm: "Повторите новый пароль",
    resetSubmit: "Установить пароль",
    resetSubmitting: "Установка…",
    resetMismatch: "Пароли не совпадают.",
    resetInvalid: "Ссылка недействительна или истекла. Запросите новую.",
    resetPolicy: "Такой пароль не принят. Выберите более длинный или менее распространённый.",
    resetError: "Не получилось. Попробуйте ещё раз.",
    resetDone: "Пароль установлен, вход выполнен.",
    resetContinue: "Продолжить",
    resetNoToken:
      "Для этой страницы нужна ссылка из письма о сбросе. Если ваша истекла — запросите новую.",
    requestNew: "Запросить новую ссылку",
  },
  kind: {
    finished_good: "Готовая продукция",
    semi_finished: "Полуфабрикат",
    raw_material: "Сырьё",
    consumable: "Расходный материал",
    service: "Услуга",
  },
  status: {
    draft: "Черновик",
    active: "Действует",
    discontinued: "Снят с производства",
  },
  notFound: {
    title: "Такой страницы нет",
    body: "Возможно, ссылка устарела или в адресе опечатка. Ничего не сломалось.",
    cta: "На главную",
  },
  roles: {
    group_admin: "Администратор группы",
    facility_admin: "Администратор площадки",
    planner: "Планировщик",
    sales: "Продажи",
    procurement: "Снабжение",
    finance: "Финансы",
    qc: "Контроль качества",
    maintenance: "Обслуживание",
    storekeeper: "Кладовщик",
    floor_worker: "Рабочий цеха",
  },
  ref: {
    none: "Не указано",
    resolving: "Ищем название…",
    copy: "Скопировать {{id}}",
    copied: "Скопировано",
    unlisted: "Другое",
  },
  // Both cells are a numeral and a symbol, and a Russian storekeeper reads
  // «200+» and «—» exactly as an English one does. They are on the ru
  // identical-by-design list in i18n-completeness.ts with that reasoning, not
  // left untranslated by omission. A worded form («не менее 200») is correct
  // Russian and does not fit a nav row.
  count: {
    atLeast: "{{count}}+",
    unknown: "—",
  },
  unit: {
    m: "м",
    km: "км",
    kg: "кг",
    t: "т",
    mm: "мм",
    mm2: "мм²",
    pcs: "шт.",
    pcs_one: "шт.",
    min: "мин",
    hr: "ч",
    l: "л",
    ma: "мА",
    ohm_per_km: "Ом/км",
    barrel: "бараб.",
    barrel_one: "бараб.",
    bobbin: "боб.",
    bobbin_one: "боб.",
    coil: "бух.",
    coil_one: "бух.",
    bag25kg: "меш. (25 кг)",
    bag25kg_one: "меш. (25 кг)",
    box: "кор.",
    box_one: "кор.",
    pallet: "пал.",
    pallet_one: "пал.",
  },
  evidence: {
    none: "Фото не приложено",
    resolving: "Открываем фото…",
    unavailable: "Это фото показать нельзя",
    open: "Открыть фото в полном размере",
    // D-030: no counted noun is agreed with its number — «Фото: 1 из 3».
    caption: "Фото: {{n}} из {{total}}",
  },
  catalogue: catalogueRu,
  onboarding: onboardingRu,
  inventory: inventoryRu,
  waste: wasteRu,
  production: productionRu,
  quality: qualityRu,
  sales: salesRu,
  messaging: messagingRu,
  public: publicRu,
  audit: auditRu,
  admin: adminRu,
  views: viewsRu,
  planning: planningRu,
  agents: agentsRu,
};
